'use client';

import { createClient } from '@/lib/supabase/client';

export interface ManagerPermission {
  id: string;
  manager_id: string;
  resource_type: 'leads' | 'quotes' | 'container_models';
  can_view: boolean;
  can_add: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_manage_photos: boolean;
  created_at: string;
  updated_at: string;
}

export interface ManagerWithPermissions {
  id: string;
  email: string;
  full_name: string;
  role: string;
  permissions: ManagerPermission[];
}

export interface PermissionChangeLog {
  id: string;
  manager_id: string;
  manager_name: string;
  resource_type: 'leads' | 'quotes' | 'container_models';
  permission_field: string;
  old_value: boolean;
  new_value: boolean;
  changed_by: string;
  changed_by_name: string;
  changed_at: string;
}

export interface GlobalManagerSettings {
  id: string;
  setting_key: string;
  setting_value: any;
  description: string;
  updated_by: string | null;
  updated_at: string;
}

export const managerService = {
  async getAllManagers(): Promise<ManagerWithPermissions[]> {
    const supabase = createClient();

    try {
      const { data: managers, error: managersError } = await supabase
        .from('user_profiles')
        .select('id, email, full_name, role')
        .in('role', ['admin', 'manager'])
        .order('created_at', { ascending: false });

      if (managersError) throw managersError;

      const managersWithPermissions = await Promise.all(
        (managers || []).map(async (manager) => {
          const { data: permissions } = await supabase
            .from('manager_permissions')
            .select('*')
            .eq('manager_id', manager.id);

          return {
            ...manager,
            permissions: permissions || [],
          };
        })
      );

      return managersWithPermissions;
    } catch (error: any) {
      console.error('Failed to fetch managers:', error.message);
      return [];
    }
  },

  async getManagerPermissions(managerId: string): Promise<ManagerPermission[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('manager_permissions')
        .select('*')
        .eq('manager_id', managerId);

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Failed to fetch manager permissions:', error.message);
      return [];
    }
  },

  async updateManagerPermissions(
    managerId: string,
    resourceType: 'leads' | 'quotes' | 'container_models',
    permissions: {
      can_view: boolean;
      can_add: boolean;
      can_edit: boolean;
      can_delete: boolean;
      can_manage_photos?: boolean;
    }
  ): Promise<boolean> {
    const supabase = createClient();

    try {
      // Get current permissions for audit log (may not exist yet)
      const { data: currentPerms } = await supabase
        .from('manager_permissions')
        .select('*')
        .eq('manager_id', managerId)
        .eq('resource_type', resourceType)
        .maybeSingle();

      // Get current user for audit log
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Update permissions
      const { error } = await supabase
        .from('manager_permissions')
        .upsert(
          {
            manager_id: managerId,
            resource_type: resourceType,
            ...permissions,
          },
          { onConflict: 'manager_id,resource_type' }
        );

      if (error) throw error;

      // Log changes if there were previous permissions
      if (currentPerms) {
        const changes = [];
        for (const [key, newValue] of Object.entries(permissions)) {
          const oldValue = currentPerms[key as keyof typeof currentPerms];
          if (oldValue !== newValue && typeof oldValue === 'boolean' && typeof newValue === 'boolean') {
            changes.push({
              manager_id: managerId,
              resource_type: resourceType,
              permission_field: key,
              old_value: oldValue,
              new_value: newValue,
              changed_by: user.id,
            });
          }
        }

        if (changes.length > 0) {
          await supabase.from('permission_change_log').insert(changes);
        }
      }

      return true;
    } catch (error: any) {
      console.error('Failed to update manager permissions:', error.message);
      return false;
    }
  },

  async getPermissionChangeLogs(): Promise<PermissionChangeLog[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('permission_change_log')
        .select(`
          *,
          manager:manager_id(full_name, email),
          changer:changed_by(full_name, email)
        `)
        .order('changed_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      return (data || []).map((log: any) => ({
        id: log.id,
        manager_id: log.manager_id,
        manager_name: log.manager?.full_name || log.manager?.email || 'Unknown',
        resource_type: log.resource_type,
        permission_field: log.permission_field,
        old_value: log.old_value,
        new_value: log.new_value,
        changed_by: log.changed_by,
        changed_by_name: log.changer?.full_name || log.changer?.email || 'Unknown',
        changed_at: log.changed_at,
      }));
    } catch (error: any) {
      console.error('Failed to fetch permission change logs:', error.message);
      return [];
    }
  },

  async getGlobalSettings(): Promise<GlobalManagerSettings[]> {
    const supabase = createClient();

    try {
      const { data, error } = await supabase
        .from('global_manager_settings')
        .select('*')
        .order('setting_key');

      if (error) throw error;
      return data || [];
    } catch (error: any) {
      console.error('Failed to fetch global settings:', error.message);
      return [];
    }
  },

  async updateGlobalSetting(
    settingKey: string,
    settingValue: any
  ): Promise<boolean> {
    const supabase = createClient();

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('global_manager_settings')
        .update({
          setting_value: settingValue,
          updated_by: user.id,
          updated_at: new Date().toISOString(),
        })
        .eq('setting_key', settingKey);

      if (error) throw error;
      return true;
    } catch (error: any) {
      console.error('Failed to update global setting:', error.message);
      return false;
    }
  },

  async hasPermission(
    resourceType: 'leads' | 'quotes' | 'container_models',
    permissionType: 'view' | 'add' | 'edit' | 'delete'
  ): Promise<boolean> {
    const supabase = createClient();

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return false;

      // Check if admin (admins have all permissions)
      const { data: profile } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();

      if (profile?.role === 'admin') return true;

      // Check manager permissions. Use maybeSingle() so that users without a
      // permission row (e.g. agents with no leads access) resolve to "no
      // permission" instead of throwing a PGRST116 / 406 error.
      const { data: permission } = await supabase
        .from('manager_permissions')
        .select('*')
        .eq('manager_id', user.id)
        .eq('resource_type', resourceType)
        .maybeSingle();

      if (!permission) return false;

      switch (permissionType) {
        case 'view':
          return permission.can_view;
        case 'add':
          return permission.can_add;
        case 'edit':
          return permission.can_edit;
        case 'delete':
          return permission.can_delete;
        default:
          return false;
      }
    } catch (error: any) {
      console.error('Failed to check permission:', error.message);
      return false;
    }
  },

  async isManager(): Promise<boolean> {
    const supabase = createClient();

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error checking manager status:', error);
        return false;
      }

      return data?.role === 'manager';
    } catch (error: any) {
      console.error('Error checking manager status:', error);
      return false;
    }
  },

  async isAgent(): Promise<boolean> {
    const supabase = createClient();

    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('user_profiles')
        .select('role')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error checking agent status:', error);
        return false;
      }

      return data?.role === 'agent';
    } catch (error: any) {
      console.error('Error checking agent status:', error);
      return false;
    }
  },
};